import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  useAuth, useShelterNotificationSettings, upsertShelterNotificationSettings, MEDICAL_EVENT_LABELS,
  useShelters, updateShelterAddress, AddressAutocompleteInput, hasVerifiedAddress, StaticMap } from '@rehome/shared';
import type { ResolvedPlace } from '@rehome/shared';
import type { MedicalEventType } from '@rehome/shared';
import { Settings, Save, Loader2, Bell, Clock, MapPin, ShieldCheck, AlertTriangle } from 'lucide-react';

// ─── ShelterSettingsPage ────────────────────────────────────────────────────
// Scoped to the shelter in the URL (/shelter/:shelterId/settings).
// Configures: how long a "Később" snooze lasts, and how many days before an
// event's next_due_date each medical event type should trigger a reminder.
// ===========================================================================

/**
 * Csak azok a típusok kapnak konfigurálható előrejelzési időt, amelyeknél
 * ÉRTELMES az ismétlődés. Az `Allergy` állapot (nem esedékessé váló esemény),
 * az `Ivartalanítás` pedig életében egyszeri beavatkozás — egyikhez sem
 * tartozik visszatérő emlékeztető, ezért kimaradnak.
 */
const REMINDER_EVENT_TYPES: MedicalEventType[] = [
  'Vaccination', 'Deworming', 'FleaTickTreatment', 'Checkup', 'Surgery', 'Medication', 'Other',
];
const DEFAULT_LEAD_DAYS: Partial<Record<MedicalEventType, number>> = {
  Vaccination: 7, Deworming: 1, FleaTickTreatment: 3, Checkup: 3, Surgery: 3, Medication: 1, Other: 1,
};
/** Bármely, itt nem szereplő típus tartaléka. */
const FALLBACK_LEAD_DAYS = 1;

const inputCls = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none bg-white disabled:bg-gray-50 disabled:text-gray-500';
const labelCls = 'block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1';

const ShelterSettingsPage: React.FC = () => {
  const { shelterId } = useParams<{ shelterId: string }>();
  const { userProfile } = useAuth();
  const { data: settings, loading, refetch } = useShelterNotificationSettings(shelterId);
  const [saving, setSaving] = useState(false);

  // ─── Menhelyi cím ───────────────────────────────────────────────────────
  const { data: shelters, loading: sheltersLoading, refetch: refetchShelters } = useShelters();
  const shelter = (shelters ?? []).find(s => s.id === shelterId) ?? null;
  const addressVerified = shelter ? hasVerifiedAddress(shelter) : false;

  const [addressInput, setAddressInput] = useState('');
  /**
   * A Google javaslatból kiválasztott hely. Amíg ez `null`, a mentés tiltott:
   * a cím csak akkor jelölhető validáltnak, ha a Google-tól származik —
   * kézzel begépelt szöveget nem fogadunk el (a DB is ezt kényszeríti ki).
   */
  const [pickedPlace, setPickedPlace] = useState<ResolvedPlace | null>(null);
  const [savingAddress, setSavingAddress] = useState(false);

  useEffect(() => {
    if (shelter) setAddressInput(shelter.location ?? '');
  }, [shelter?.id, shelter?.location]);

  const handleSaveAddress = async () => {
    if (!shelterId || !pickedPlace) return;
    setSavingAddress(true);
    const ok = await updateShelterAddress({
      shelterId,
      address: pickedPlace.address,
      lat: pickedPlace.lat,
      lng: pickedPlace.lng,
      placeId: pickedPlace.placeId,
    });
    setSavingAddress(false);
    if (ok) {
      setPickedPlace(null);
      refetchShelters();
    }
  };

  const [snoozeMinutes, setSnoozeMinutes] = useState('60');
  const [leadDays, setLeadDays] = useState<Record<MedicalEventType, string>>(
    Object.fromEntries(REMINDER_EVENT_TYPES.map(t => [t, String((DEFAULT_LEAD_DAYS[t] ?? FALLBACK_LEAD_DAYS))])) as Record<MedicalEventType, string>
  );

  useEffect(() => {
    setSnoozeMinutes(String(settings?.snoozeDurationMinutes ?? 60));
    setLeadDays(prev => {
      const next = { ...prev };
      REMINDER_EVENT_TYPES.forEach(t => {
        next[t] = String(settings?.leadDaysByType?.[t] ?? (DEFAULT_LEAD_DAYS[t] ?? FALLBACK_LEAD_DAYS));
      });
      return next;
    });
  }, [settings]);

  // Same permission model used across the admin app (see AnimalProfilePage.tsx) —
  // RLS enforces the actual write permission, this only controls UI affordances.
  const canEdit = userProfile?.role === 'shelter_admin' || userProfile?.role === 'super_admin';

  const handleSave = async () => {
    if (!shelterId) return;
    setSaving(true);
    await upsertShelterNotificationSettings({
      shelterId,
      snoozeDurationMinutes: parseInt(snoozeMinutes, 10) || 60,
      leadDaysByType: Object.fromEntries(
        REMINDER_EVENT_TYPES.map(t => [t, parseInt(leadDays[t], 10) || (DEFAULT_LEAD_DAYS[t] ?? FALLBACK_LEAD_DAYS)])
      ) as Partial<Record<MedicalEventType, number>>,
    });
    setSaving(false);
    refetch();
  };

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto space-y-6">
        <div className="animate-pulse h-40 bg-gray-200 rounded-xl" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6 pb-12">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3"><Settings className="w-8 h-8 text-teal-600" />Beállítások</h1>
        <p className="text-gray-600 mt-1">A menhely címe és az egészségügyi emlékeztetők időzítése</p>
      </div>

      {!canEdit && (
        <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
          Csak menhely admin módosíthatja ezeket a beállításokat — az alábbi értékek jelenleg csak megtekinthetők.
        </p>
      )}

      {/* ── Menhely címe ─────────────────────────────────────────────────
          A cím KÖTELEZŐEN a Google javaslataiból származik: ez adja a
          koordinátát, ami nélkül a platform Smart Matche nem tudja
          kiszámolni, milyen messze van a menhely az érdeklődőtől. */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 space-y-4">
        <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
          <MapPin className="w-5 h-5 text-teal-600" />A menhely címe
        </h2>

        {sheltersLoading && !shelter ? (
          <p className="text-sm text-gray-400">Betöltés...</p>
        ) : !shelter ? (
          <p className="text-sm text-gray-400">A menhely adatai nem érhetők el.</p>
        ) : (
          <>
            {addressVerified ? (
              <div className="flex items-start gap-2 rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-800">
                <ShieldCheck className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold">Validált cím</p>
                  <p className="text-green-700/80">
                    {shelter.location}
                    {shelter.addressVerifiedAt && (
                      <span className="text-green-700/60">
                        {' '}· ellenőrizve {new Date(shelter.addressVerifiedAt).toLocaleDateString('hu-HU')}
                      </span>
                    )}
                  </p>
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold">A cím még nincs validálva</p>
                  <p className="text-amber-700/80">
                    Jelenlegi érték: <span className="font-medium">{shelter.location || '— nincs megadva —'}</span>.
                    Válaszd ki a pontos címet a Google javaslatai közül — enélkül a menhely állatai
                    nem kapnak távolság szerinti pontszámot a platform Smart Matchében.
                  </p>
                </div>
              </div>
            )}

            {canEdit ? (
              <div className="space-y-3">
                <div>
                  <label className={labelCls} htmlFor="shelter-address">Cím keresése</label>
                  <AddressAutocompleteInput
                    id="shelter-address"
                    value={addressInput}
                    onChange={value => { setAddressInput(value); setPickedPlace(null); }}
                    onPlaceSelected={place => { setAddressInput(place.address); setPickedPlace(place); }}
                    placeholder="Pl. Kecskemét, Izsáki út 10."
                    className={inputCls}
                    hint="Legalább 3 karakter után jönnek a javaslatok. A mentéshez a listából kell választani."
                  />
                </div>

                {/* A kiválasztott cím térképen: a Google javaslatai közt
                    hasonló nevű települések is szerepelnek, és a menhely
                    koordinátája adja a Smart Match távolság-pontozását —
                    egy rossz találat csendben rontaná el a párosítást. */}
                {pickedPlace && (
                  <StaticMap
                    lat={pickedPlace.lat}
                    lng={pickedPlace.lng}
                    label={pickedPlace.address}
                    height={160}
                  />
                )}

                {pickedPlace && (
                  <p className="text-xs text-gray-500 flex items-center gap-1.5">
                    <ShieldCheck className="w-3.5 h-3.5 text-green-600 flex-shrink-0" />
                    Kiválasztva: <span className="font-medium text-gray-700">{pickedPlace.address}</span>
                    <span className="font-mono text-gray-400">
                      ({pickedPlace.lat.toFixed(5)}, {pickedPlace.lng.toFixed(5)})
                    </span>
                  </p>
                )}

                <div className="flex justify-end">
                  <button
                    onClick={handleSaveAddress}
                    disabled={!pickedPlace || savingAddress}
                    className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-semibold hover:bg-teal-700 transition-colors disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed"
                  >
                    {savingAddress ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    Cím mentése
                  </button>
                </div>
              </div>
            ) : (
              <p className="text-sm text-gray-500">A cím módosításához menhely admin jogosultság szükséges.</p>
            )}
          </>
        )}
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 space-y-4">
        <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2"><Clock className="w-5 h-5 text-teal-600" />Elhalasztás ("Később")</h2>
        <p className="text-sm text-gray-500">
          Ha egy dolgozó a "Később" gombra kattint egy értesítésnél, ennyi idő múlva jelenik meg újra mindenkinek.
        </p>
        <div className="max-w-xs">
          <label className={labelCls}>Elhalasztás időtartama (perc)</label>
          <input
            type="number" min="1" value={snoozeMinutes}
            onChange={e => setSnoozeMinutes(e.target.value)}
            className={inputCls} disabled={!canEdit}
          />
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 space-y-4">
        <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2"><Bell className="w-5 h-5 text-teal-600" />Emlékeztető előrejelzés típusonként</h2>
        <p className="text-sm text-gray-500">
          Hány nappal a következő esedékesség előtt kapjon a menhely emlékeztetőt, eseménytípusonként.
          Egy adott bejegyzésnél az állat egészségügyi lapján felülírható.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {REMINDER_EVENT_TYPES.map(t => (
            <div key={t}>
              <label className={labelCls}>{MEDICAL_EVENT_LABELS[t]}</label>
              <div className="flex items-center gap-2">
                <input
                  type="number" min="0" value={leadDays[t]}
                  onChange={e => setLeadDays(prev => ({ ...prev, [t]: e.target.value }))}
                  className={inputCls} disabled={!canEdit}
                />
                <span className="text-xs text-gray-400 whitespace-nowrap">nap előtte</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {canEdit && (
        <div className="flex justify-end">
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-semibold hover:bg-teal-700 transition-colors disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Beállítások mentése
          </button>
        </div>
      )}
    </div>
  );
};

export default ShelterSettingsPage;
